import {
  getEmployeeFileInquiry,
  getFileInquiries,
  getFileInquiry,
  getFileInquiryHistory,
} from "@api/fileInquiry";
import {
  EmployeeFileInquiryResponseType,
  FileInquiryHistoryResponse,
  FileInquiryListResponse,
  InquiryFile,
} from "@type/HRM/fileInquiry";
import { useEffect, useState } from "react";

export const useFileInquiry = (id: string | null, fileId?: string) => {
  const [isLoading, setIsLoading] = useState(false);
  const [employeeFileInquiry, setEmployeeFileInquiry] =
    useState<EmployeeFileInquiryResponseType>();
  const [fileInquiry, setFileInquiry] = useState<InquiryFile>();
  const [isEditMode, setIsEditMode] = useState(false);
  const [fileInquiryList, setFileInquiryList] =
    useState<FileInquiryListResponse>();
  const [fileInquiryHistory, setFileInquiryHistory] =
    useState<FileInquiryHistoryResponse>();

  const [query, setQuery] = useState({
    page: 1,
    size: 10,
    search: "",
  });

  const handleQuery = (key: string, value: number | boolean | string | null) =>
    setQuery((prev) => ({
      ...prev,
      [key]: value,
    }));

  useEffect(() => {
    const fetchFileInquiries = async () => {
      setIsLoading(true);
      await getFileInquiries(query, (response) => {
        if (response.status === "success") {
          setFileInquiryList(response?.data?.data);
        } else {
          console.error(
            "Failed to fetch employee file inquiry:",
            response.data,
          );
        }
        setIsLoading(false);
      });
    };
    fetchFileInquiries();
  }, [query]);

  useEffect(() => {
    const fetchFileInquiry = async () => {
      setIsLoading(true);

      await getEmployeeFileInquiry(id || "", (response) => {
        if (response.status === "success") {
          setEmployeeFileInquiry(response.data?.data);
        } else {
          console.error("Failed to fetch file inquiry:", response.data);
        }
        setIsLoading(false);
      });
    };
    if (id) {
      fetchFileInquiry();
    }
  }, [id]);

  const fetchSingleFileInquiry = async (fileInquiryId: number) => {
    setIsLoading(true);

    await getFileInquiry(fileInquiryId, (response) => {
      if (response.status === "success") {
        setFileInquiry(response.data?.data);
      } else {
        console.error("Failed to fetch single file inquiry:", response.data);
      }
      setIsLoading(false);
    });
  };

  const handleEditMode = (isEdit: boolean) => {
    setIsEditMode(isEdit);
  };

  useEffect(() => {
    const fetchFileInquiryHistory = async () => {
      if (fileId) {
        setIsLoading(true);
        await getFileInquiryHistory(fileId, (response) => {
          if (response.status === "success") {
            setFileInquiryHistory(response.data?.data);
          } else {
            console.error(
              "Failed to fetch file inquiry history:",
              response.data,
            );
          }
          setIsLoading(false);
        });
      }
    };
    fetchFileInquiryHistory();
  }, [fileId]);

  const refetchFileInquiryHistory = async (
    fileIdForRefetch: string | undefined,
  ): Promise<void> => {
    if (fileIdForRefetch) {
      setIsLoading(true);
      await getFileInquiryHistory(fileIdForRefetch, (response) => {
        if (response.status === "success") {
          setFileInquiryHistory(response.data?.data);
        } else {
          console.error(
            "Failed to refetch file inquiry history:",
            response.data,
          );
        }
        setIsLoading(false);
      });
    }
  };

  const reFetchFileInquiry = async (employeeId: string | undefined) => {
    setIsLoading(true);

    await getEmployeeFileInquiry(employeeId || "", (response) => {
      if (response.status === "success") {
        setEmployeeFileInquiry(response.data?.data);
      } else {
        console.error("Failed to fetch file inquiry:", response.data);
      }
      setIsLoading(false);
    });
  };

  return {
    isLoading,
    employeeFileInquiry,
    fetchSingleFileInquiry,
    fileInquiry,
    isEditMode,
    handleEditMode,
    fileInquiryList,
    handleQuery,
    fileInquiryHistory,
    refetchFileInquiryHistory,
    reFetchFileInquiry,
  };
};
